import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Ticket, Lock } from "@phosphor-icons/react";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const LoginPage = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || "/dashboard";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            await login(email, password);
            navigate(from, { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-split">
            {/* Left side - Login form */}
            <div className="flex flex-col justify-center px-8 lg:px-16 py-12 bg-white">
                <div className="max-w-md w-full mx-auto">
                    {/* Logo/Brand */}
                    <div className="mb-12">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center">
                                <Ticket weight="bold" className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                OPEN TASKS
                            </span>
                        </div>
                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mt-4">
                            Ticket Management
                        </p>
                    </div>

                    {/* Login Form */}
                    <div className="mb-8">
                        <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            Sign In
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Enter your credentials to access the dashboard
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div 
                                data-testid="login-error-message"
                                className="p-4 border border-destructive bg-destructive/5 text-destructive text-sm"
                            >
                                {error}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label 
                                htmlFor="email" 
                                className="text-xs font-bold uppercase tracking-widest text-muted-foreground"
                            >
                                Email Address
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                data-testid="login-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="h-12 border-2 focus:border-[#002FA7] focus:ring-0"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label 
                                htmlFor="password"
                                className="text-xs font-bold uppercase tracking-widest text-muted-foreground"
                            >
                                Password
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                data-testid="login-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                required
                                className="h-12 border-2 focus:border-[#002FA7] focus:ring-0"
                            />
                        </div>

                        <Button
                            type="submit"
                            data-testid="login-submit-button"
                            disabled={loading}
                            className="w-full h-12 bg-[#002FA7] hover:bg-[#002482] text-white font-bold uppercase tracking-widest text-sm border-0"
                        >
                            {loading ? (
                                <span className="font-mono">AUTHENTICATING...</span>
                            ) : (
                                <>
                                    <Lock weight="bold" className="w-4 h-4 mr-2" />
                                    Sign In
                                </>
                            )}
                        </Button>
                    </form>
                </div>
            </div>

            {/* Right side - Hero image */}
            <div 
                className="login-hero bg-cover bg-center relative"
                style={{
                    backgroundImage: `url('https://images.unsplash.com/photo-1763929154494-a3d6eba05282?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJsdWUlMjB3aGl0ZSUyMHBhdHRlcm58ZW58MHx8fHwxNzc1MTYyMTI4fDA&ixlib=rb-4.1.0&q=85')`
                }}
            >
                <div className="absolute inset-0 bg-black/5" />
            </div>
        </div>
    );
};

export default LoginPage;
